import { useEffect, useRef, useState } from "react";
import { CircleStop, X } from "lucide-react";

import { haltCall } from "../../api/calls";
import type { CallListItem } from "../../api/calls";

type Phase = "idle" | "halting" | "timeout";

interface Props {
  callId: string;
  /** Source of truth from useCall — used to know when worker actually halts. */
  liveCall: CallListItem;
}

const HALT_TIMEOUT_MS = 10_000;

/**
 * Safety-critical control. Two-step confirmation: operator must type HALT,
 * provide a reason (audit-chain logged), then the worker is asked to drop
 * the current turn and end the session. Designed to feel weighty — sharp
 * red border on the trigger, danger-tinted modal, status box during the
 * 10s acknowledgement window.
 */
export function HaltButton({ callId, liveCall }: Props) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>("idle");
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  // When the parent's cached state transitions away from LIVE, resolve.
  useEffect(() => {
    if (phase === "halting" && liveCall.state !== "LIVE") {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      setPhase("idle");
      setOpen(false);
      setConfirm("");
      setReason("");
    }
  }, [liveCall.state, phase]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  async function submitHalt() {
    setError(null);
    setPhase("halting");
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      setPhase("timeout");
    }, HALT_TIMEOUT_MS);
    try {
      await haltCall(callId, reason || "operator_decision");
    } catch (err) {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      setPhase("idle");
      setError(err instanceof Error ? err.message : "halt request failed");
    }
  }

  return (
    <>
      <button
        onClick={() => {
          setOpen(true);
          setPhase("idle");
          setError(null);
        }}
        style={triggerStyle}
        title="Halt this call (audit-chain logged)"
      >
        <CircleStop size={12} strokeWidth={2} />
        HALT
      </button>

      {open ? (
        <div
          role="dialog"
          style={backdropStyle}
          onClick={() => {
            if (phase === "idle") setOpen(false);
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={panelStyle}>
            {/* top accent line — danger red */}
            <span
              aria-hidden
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 1,
                background:
                  "linear-gradient(90deg, transparent, var(--error), transparent)",
              }}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 6,
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    letterSpacing: "0.20em",
                    color: "var(--error)",
                    marginBottom: 6,
                  }}
                >
                  Safety halt
                </div>
                <h3
                  style={{
                    margin: 0,
                    fontFamily: "var(--font-display)",
                    fontSize: 22,
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                    color: "var(--text-primary)",
                  }}
                >
                  Halt this call?
                </h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                disabled={phase === "halting"}
                aria-label="Close"
                style={closeIconStyle}
              >
                <X size={14} strokeWidth={1.5} />
              </button>
            </div>

            <p style={bodyText}>
              The worker will be told to drop the current turn and end the session.
              This interrupts a real customer touchpoint. Type{" "}
              <code
                style={{
                  fontFamily: "var(--font-mono)",
                  color: "var(--error)",
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                }}
              >
                HALT
              </code>{" "}
              to confirm. Your operator identity and the reason below are recorded
              to the audit chain.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 14 }}>
              <Field label="Confirmation">
                <input
                  autoFocus
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  style={inputStyle()}
                  placeholder="type HALT"
                  disabled={phase !== "idle"}
                />
              </Field>
              <Field label="Reason (audit-logged)">
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. agent went off-script on turn 4"
                  style={{
                    ...inputStyle(),
                    minHeight: 64,
                    fontFamily: "var(--font-sans)",
                    fontSize: 12,
                    letterSpacing: "-0.005em",
                    resize: "vertical",
                  }}
                  disabled={phase !== "idle"}
                />
              </Field>
            </div>

            {phase === "halting" ? (
              <StatusRow tone="warn">Halting… waiting for worker to drop the call.</StatusRow>
            ) : null}

            {phase === "timeout" ? (
              <StatusRow tone="error">
                Worker did not acknowledge within 10s.
                <button
                  onClick={() => {
                    setPhase("idle");
                    void submitHalt();
                  }}
                  style={retryStyle}
                >
                  Retry
                </button>
              </StatusRow>
            ) : null}

            {error ? <StatusRow tone="error">{error}</StatusRow> : null}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 18,
                paddingTop: 14,
                borderTop: "1px solid var(--border-subtle)",
              }}
            >
              <button
                onClick={() => setOpen(false)}
                disabled={phase === "halting"}
                style={cancelButtonStyle}
              >
                CANCEL
              </button>
              <button
                disabled={confirm !== "HALT" || phase !== "idle"}
                onClick={() => {
                  void submitHalt();
                }}
                style={
                  confirm === "HALT" && phase === "idle"
                    ? haltActionEnabled
                    : haltActionDisabled
                }
              >
                {phase === "halting" ? "HALTING…" : "HALT CALL"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9.5,
          letterSpacing: "0.16em",
          color: "var(--text-muted)",
          textTransform: "uppercase",
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function StatusRow({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "warn" | "error";
}) {
  const color = tone === "error" ? "var(--error)" : "var(--warning)";
  return (
    <div
      style={{
        marginTop: 14,
        padding: "10px 12px",
        border: `1px solid ${color}`,
        background: tone === "error" ? "rgba(244,63,94,0.08)" : "rgba(245,158,11,0.08)",
        borderRadius: "var(--radius-input)",
        color,
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        letterSpacing: "0.04em",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      {children}
    </div>
  );
}

const triggerStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "7px 14px",
  borderRadius: "var(--radius-input)",
  background: "transparent",
  border: "1px solid var(--error)",
  color: "var(--error)",
  cursor: "pointer",
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  letterSpacing: "0.16em",
  fontWeight: 600,
  textTransform: "uppercase",
};

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "var(--modal-backdrop)",
  backdropFilter: "blur(2px)",
  display: "grid",
  placeItems: "center",
  zIndex: 10,
};

const panelStyle: React.CSSProperties = {
  position: "relative",
  background: "var(--surface-overlay)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-card)",
  padding: "22px 24px",
  width: 480,
  maxWidth: "92vw",
  color: "var(--text-primary)",
  overflow: "hidden",
  boxShadow: "0 30px 80px rgba(0,0,0,0.55)",
};

const bodyText: React.CSSProperties = {
  margin: "12px 0 0",
  fontSize: 12.5,
  color: "var(--text-secondary)",
  lineHeight: 1.6,
  letterSpacing: "-0.005em",
};

const closeIconStyle: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--border-subtle)",
  borderRadius: "var(--radius-input)",
  color: "var(--text-muted)",
  cursor: "pointer",
  display: "grid",
  placeItems: "center",
  width: 26,
  height: 26,
};

const cancelButtonStyle: React.CSSProperties = {
  padding: "8px 14px",
  borderRadius: "var(--radius-input)",
  background: "transparent",
  border: "1px solid var(--border)",
  color: "var(--text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  letterSpacing: "0.14em",
  fontWeight: 500,
};

const haltActionEnabled: React.CSSProperties = {
  padding: "8px 16px",
  borderRadius: "var(--radius-input)",
  background: "var(--error)",
  color: "#FFFFFF",
  border: "1px solid var(--error)",
  cursor: "pointer",
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  letterSpacing: "0.16em",
  fontWeight: 600,
  boxShadow: "var(--glow-error)",
};

const haltActionDisabled: React.CSSProperties = {
  padding: "8px 16px",
  borderRadius: "var(--radius-input)",
  background: "var(--bg-tertiary)",
  color: "var(--text-muted)",
  border: "1px solid var(--border-subtle)",
  cursor: "not-allowed",
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  letterSpacing: "0.16em",
  fontWeight: 500,
};

const retryStyle: React.CSSProperties = {
  marginLeft: "auto",
  padding: "4px 12px",
  borderRadius: "var(--radius-input)",
  border: "1px solid var(--error)",
  background: "transparent",
  color: "var(--error)",
  cursor: "pointer",
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.14em",
  fontWeight: 600,
};

function inputStyle(): React.CSSProperties {
  return {
    width: "100%",
    background: "var(--bg-input)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-input)",
    padding: "9px 12px",
    color: "var(--text-primary)",
    fontSize: 12,
    fontFamily: "var(--font-mono)",
    letterSpacing: "0.04em",
    boxSizing: "border-box",
    outline: "none",
  };
}
